// The shell a stepped connection flow renders in (#599), whichever service it connects.
//
// It knows a flow has a title, an ordered list of steps and one current step, and
// nothing about what any step asks: the Jira flow (`JiraFlow.tsx`) is its first caller
// and a Slack flow is meant to be its second. Position is not a claim that earlier
// steps succeeded, which is why the list is not a set of links.

import type { ReactNode } from "react";

/** One step: what the flow calls it, and what a person reads. */
export interface FlowStep<S extends string = string> {
  id: S;
  label: string;
}

const positionOf = <S extends string>(steps: ReadonlyArray<FlowStep<S>>, current: S): number =>
  Math.max(
    0,
    steps.findIndex((step) => step.id === current),
  );

/** The step after `current`, or `current` itself at the end. */
export function stepAfter<S extends string>(steps: ReadonlyArray<FlowStep<S>>, current: S): S {
  return steps[Math.min(positionOf(steps, current) + 1, steps.length - 1)].id;
}

/** The step before `current`, or `current` itself at the start. */
export function stepBefore<S extends string>(steps: ReadonlyArray<FlowStep<S>>, current: S): S {
  return steps[Math.max(positionOf(steps, current) - 1, 0)].id;
}

export function StepFlow<S extends string>({
  title,
  testId,
  steps,
  current,
  children,
}: {
  title: string;
  testId: string;
  steps: ReadonlyArray<FlowStep<S>>;
  current: S;
  children?: ReactNode;
}) {
  const at = positionOf(steps, current);
  return (
    <section aria-label={title} data-testid={testId} className="panel__body stack stack--tight step-flow">
      <h3>{title}</h3>
      <ol className="step-flow__steps" aria-label={`${title}: steps`}>
        {steps.map((step, index) => (
          <li key={step.id} aria-current={step.id === current ? "step" : undefined} data-done={index < at ? "" : undefined}>
            <span aria-hidden="true">{index + 1}</span>
            {step.label}
          </li>
        ))}
      </ol>
      <p className="muted step-flow__position">
        Step {at + 1} of {steps.length}
      </p>
      {children}
    </section>
  );
}
